import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pulse as Activity, Lightning, Lock, CheckCircle, MapPin, ArrowRight, SpinnerGap
} from '@phosphor-icons/react';
import axios from 'axios';
import { useWsSubscribe } from '../../contexts/WebSocketContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Human-friendly relative time, e.g. "2m ago" / "just now"
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const TYPE_META = {
  posted: { Icon: Lightning, bg: 'bg-[#3B82F6]', label: 'Posted', verb: 'New', cls: 'text-[#475569]' },
  reserved: { Icon: Lock, bg: 'bg-emerald-500', label: 'Reserved', verb: 'Held', cls: 'text-emerald-600' },
  sold: { Icon: CheckCircle, bg: 'bg-neutral-700', label: 'Sold', verb: 'Sold', cls: 'text-neutral-700' },
};

/**
 * Recent Activity Feed — anonymous live feed of platform events.
 * Shown on the Contractor Dashboard. Drives FOMO + conversion.
 *
 * Auto-refreshes every 30s and subscribes to WS reserve/release for instant updates.
 */
export const RecentActivityFeed = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [soldToday, setSoldToday] = useState(0);
  const [myArea, setMyArea] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0); // forces "time ago" rerenders

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/leads/activity-feed?limit=12`, { headers: authHeader() });
      setEvents(res.data.events || []);
      setSoldToday(res.data.sold_today_in_area || 0);
      setMyArea(res.data.my_area || '');
    } catch (err) {
      console.error('activity feed load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 30_000);
    return () => clearInterval(id);
  }, [fetchFeed]);

  // Rerender every 30s so timeAgo strings update
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // React to WS reservation events instantly so the feed feels truly live
  const handleWsMessage = useCallback((msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === 'lead_reserved' && msg.lead_id) {
      setEvents(prev => {
        // Avoid duplicates if reserving a lead we already saw posted
        const filtered = prev.filter(e => !(e.lead_id === msg.lead_id && e.type === 'reserved'));
        return [
          { type: 'reserved', lead_id: msg.lead_id, at: new Date().toISOString(), city: '', state: '', job_type: 'lead' },
          ...filtered
        ].slice(0, 12);
      });
    }
    if (msg.type === 'new_lead' && msg.lead) {
      setEvents(prev => [
        {
          type: 'posted',
          lead_id: msg.lead.lead_id,
          at: new Date().toISOString(),
          city: msg.lead.city || '',
          state: msg.lead.state || '',
          job_type: (msg.lead.job_type_display || msg.lead.job_type || 'lead').toLowerCase(),
          grade: msg.lead.lead_grade,
        },
        ...prev.filter(e => !(e.lead_id === msg.lead.lead_id && e.type === 'posted'))
      ].slice(0, 12));
    }
  }, []);
  useWsSubscribe(handleWsMessage);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="recent-activity-feed">
        <div className="flex items-center gap-2 text-neutral-500">
          <SpinnerGap size={18} weight="bold" className="animate-spin" />
          <span className="text-sm">Loading activity…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2563EB]"
      data-testid="recent-activity-feed"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg border-2 border-black bg-[#2563EB] flex items-center justify-center">
            <Activity size={18} weight="bold" className="text-white" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-neutral-500 dark:text-neutral-400">Live Marketplace</p>
            <h3 className="font-black text-neutral-900 dark:text-white text-base leading-tight">Recent Activity</h3>
          </div>
        </div>
        <button
          onClick={() => navigate('/leads-marketplace')}
          className="text-xs font-bold text-[#2B4C6F] hover:underline flex items-center gap-1"
          data-testid="activity-feed-view-all"
        >
          View all
          <ArrowRight size={12} weight="bold" />
        </button>
      </div>

      {soldToday >= 3 && myArea && (
        <div className="mb-3 p-2.5 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded">
          <p className="text-xs font-bold text-amber-800 dark:text-amber-300" data-testid="sold-today-rollup">
            <Lightning size={12} weight="fill" className="inline mr-1 text-amber-600" />
            <strong>{soldToday}</strong> {soldToday === 1 ? 'lead' : 'leads'} sold in {myArea} today — don&apos;t miss the next one.
          </p>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-6">
          <Activity size={32} weight="light" className="text-neutral-300 mx-auto mb-2" />
          <p className="text-sm text-neutral-500">No activity yet. Check back soon.</p>
        </div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="activity-events">
          {events.map((e, idx) => {
            const meta = TYPE_META[e.type] || TYPE_META.posted;
            const Icon = meta.Icon;
            const cityLabel = [e.city, e.state].filter(Boolean).join(', ') || 'your area';
            const headline =
              e.type === 'posted' ? `New ${e.job_type} lead in ${cityLabel}` :
              e.type === 'reserved' ? `Contractor holding a ${e.job_type} lead in ${cityLabel}` :
              `${e.job_type} lead just sold in ${cityLabel}`;
            return (
              <li
                key={`${e.type}-${e.lead_id}-${idx}`}
                className="flex items-start gap-2.5 p-2 rounded-lg border border-neutral-100 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors"
                data-testid={`activity-event-${idx}`}
              >
                <div className={`w-7 h-7 rounded ${meta.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon size={14} weight="bold" className="text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-neutral-900 dark:text-white truncate">{headline}</p>
                  <p className="text-[10px] text-neutral-500 dark:text-neutral-400 flex items-center gap-2 mt-0.5">
                    <span className={`font-black ${meta.cls} uppercase tracking-wider`}>{meta.verb}</span>
                    <span>·</span>
                    <span>{timeAgo(e.at)}</span>
                    {e.grade && (
                      <>
                        <span>·</span>
                        <span className="font-bold">Grade {e.grade}</span>
                      </>
                    )}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentActivityFeed;
